import type { CapturedPhotoAsset } from '@/shared/camera/photo-capture-adapter';

const THUMBNAIL_MAX_EDGE = 320;

export async function createCapturedPhotoAssetFromFile(file: File): Promise<CapturedPhotoAsset> {
  const capturedAt = new Date().toISOString();
  const dimensions = await readImageDimensions(file);
  const thumbnailBlob = await createThumbnailBlob(file, dimensions);

  return {
    capturedAt,
    height: dimensions.height,
    mimeType: file.type || 'application/octet-stream',
    originalBlob: file,
    sha256: await hashBlobSha256(file),
    thumbnailBlob,
    width: dimensions.width,
  };
}

async function readImageDimensions(file: File) {
  if (typeof globalThis.createImageBitmap !== 'function') {
    return { height: 0, width: 0 };
  }

  try {
    const bitmap = await globalThis.createImageBitmap(file);
    const dimensions = {
      height: bitmap.height,
      width: bitmap.width,
    };
    bitmap.close();

    return dimensions;
  } catch {
    return { height: 0, width: 0 };
  }
}

async function createThumbnailBlob(file: File, dimensions: { height: number; width: number }) {
  if (
    typeof globalThis.createImageBitmap !== 'function' ||
    typeof globalThis.document === 'undefined' ||
    dimensions.width <= 0 ||
    dimensions.height <= 0
  ) {
    return file;
  }

  try {
    const bitmap = await globalThis.createImageBitmap(file);
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = globalThis.document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    context?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    return await canvasToBlob(canvas, file.type || 'image/jpeg');
  } catch {
    return file;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string) {
  return new Promise<Blob>(resolve => {
    canvas.toBlob(blob => resolve(blob ?? new Blob([], { type: mimeType })), mimeType, 0.82);
  });
}

async function hashBlobSha256(blob: Blob) {
  if (!globalThis.crypto?.subtle) {
    return undefined;
  }

  try {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());

    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}
